import React, { memo, ReactNode } from 'react';
import MuiModal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { Backdrop, SxProps, Theme } from '@mui/material';

interface ModalHeaderProps {
  children: ReactNode;
}

interface ModalBodyProps {
  children: ReactNode;
}

interface ModalFooterProps {
  children: ReactNode;
  onClose?: () => void;
}

interface CustomModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  sx?: SxProps<Theme>;
}

const ModalHeader = memo<ModalHeaderProps>(({ children }) => (
  <div style={{ marginBottom: 0 }}>
    {children}
  </div>
));

ModalHeader.displayName = 'ModalHeader';

const ModalBody = memo<ModalBodyProps>(({ children }) => (
  <div className="noScrollBar" style={{ marginBottom: 0, maxHeight: '90vh', overflow: 'scroll',height:'100%' }}>
    {children}
  </div>
));

ModalBody.displayName = 'ModalBody';

const ModalFooter = memo<ModalFooterProps>(({ children }) => (
  <div style={{ boxShadow: '0px 25px 57px rgba(0, 0, 0, 0.5)' }}>
    {children}
  </div>
));

ModalFooter.displayName = 'ModalFooter';

const CustomModalComponent = memo<CustomModalProps>(({ open, onClose, children, sx = {} }) => (
  <MuiModal
    className=""
    id={open ? 'ModalWhileSave' : ''}
    open={open}
    onClose={(event, reason) => {
      // Prevent closing on backdrop click
      if (reason === 'backdropClick') {
        return;
      }
      onClose();
    }}
    sx={sx}
    closeAfterTransition
    disableRestoreFocus
    BackdropComponent={Backdrop}
    BackdropProps={{
      timeout: 500,
    }}
  >
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: 'flex',
        '@media (min-width:600px)': {
          width: 'auto',
        },
        width: '90%',
        flexDirection: 'column',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        boxShadow: 24,
        maxHeight: '95vh',
        overflow: 'hidden',
        ...(sx as object),
      }}
    >
      {children}
    </Box>
  </MuiModal>
));

CustomModalComponent.displayName = 'Modal';

// Attach sub-components as static properties
(CustomModalComponent as any).Header = ModalHeader;
(CustomModalComponent as any).Body = ModalBody;
(CustomModalComponent as any).Footer = ModalFooter;

// Create the final exported Modal with proper typing
interface ModalWithSubComponents extends React.NamedExoticComponent<CustomModalProps> {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
}

const Modal: ModalWithSubComponents = CustomModalComponent as ModalWithSubComponents;

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export { Modal };

